import { WalletSyncRunStatus, type WalletSyncStatus } from '@/api/portfolio/queries';
import type { WalletSyncResponse } from '@/api/wallet/queries';

export interface TrackedWalletSyncRun {
  runId: string;
  kind: 'balances' | 'history';
  requestedAt: string;
}

export const isActiveWalletSyncRun = (status: string): boolean =>
  status === WalletSyncRunStatus.Queued || status === WalletSyncRunStatus.Running;

export const getActiveWalletSyncRuns = (response: WalletSyncResponse): TrackedWalletSyncRun[] =>
  [response.balances, response.history]
    .filter((run) => isActiveWalletSyncRun(run.status))
    .map(({ runId, kind, requestedAt }) => ({ runId, kind, requestedAt }));

export const mergeWalletSyncRuns = (
  current: readonly TrackedWalletSyncRun[],
  additional: readonly TrackedWalletSyncRun[],
): TrackedWalletSyncRun[] => {
  const runsById = new Map(current.map((run) => [run.runId, run]));
  additional.forEach((run) => runsById.set(run.runId, run));
  return [...runsById.values()];
};

const isNewerRun = (
  candidate: Pick<TrackedWalletSyncRun, 'requestedAt'>,
  tracked: Pick<TrackedWalletSyncRun, 'requestedAt'>,
): boolean => {
  const candidateTime = Date.parse(candidate.requestedAt);
  const trackedTime = Date.parse(tracked.requestedAt);
  return (
    Number.isFinite(candidateTime) && Number.isFinite(trackedTime) && candidateTime > trackedTime
  );
};

export const getTerminalWalletSyncRunIds = (
  status: WalletSyncStatus,
  pendingRuns: readonly TrackedWalletSyncRun[],
): string[] => {
  const runs = [status.balances, status.history].filter(
    (run): run is NonNullable<typeof run> => run !== null,
  );

  return pendingRuns.flatMap((pendingRun) => {
    const exactRun = runs.find((candidate) => candidate.runId === pendingRun.runId);
    if (exactRun) {
      return isActiveWalletSyncRun(exactRun.status) ? [] : [pendingRun.runId];
    }

    const successor = runs.find(
      (candidate) => candidate.kind === pendingRun.kind && isNewerRun(candidate, pendingRun),
    );
    return successor && !isActiveWalletSyncRun(successor.status) ? [pendingRun.runId] : [];
  });
};
